<template>
    <div class="row" @click="goToInstallation(installation.id)">
        <div class="col-xs-1">
            <span
                class="vrm-block__inline_icon"
                :class="convertDeviceIcon(installation.device_icon)"
                :tooltip="$t('core.running_on_' + installation.device_icon)"
            ></span>
        </div>
        <div class="col-xs-11">
            <h4>{{ installation.name }}</h4>
            <span
                v-if="installation.alarm"
                class="vrm-block__inline_icon sg-vrm-icon_012_alarm-32px sg-color-red"
            ></span>
            <span v-if="installation.alarm" class="vrm-block__error">
                {{ $t('installationoverview.alarm', { i18nFallback: 'Alarm' }) }}
            </span>
            <span>
                {{ $t('installationoverview.last_update', { i18nFallback: 'Last update' }) }}:
            </span>
            <span>{{ getLastUpdate(installation) }}</span>

            <vrm-scroll-fade class="vrm-scroll-fade--vrm-block" :fade-enabled="fadeEnabled">
                <div class="vrm-block__scroll">
                    <div
                        v-if="
                            propRead(installation, 'bs') ||
                                propRead(installation, 'soc_status', 'textValue')
                        "
                        class="vrm-block__scroll__item"
                        :class="
                            propRead(installation, 'bs', 'rawValue') && {
                                'sg-color-red': propRead(installation, 'bs', 'rawValue') < 20,
                                'sg-color-orange': propRead(installation, 'bs', 'rawValue') < 60,
                            }
                        "
                    >
                        <label class="vrm-block__scroll__label">
                            {{
                                $t('installationoverview.stateOfCharge', {
                                    i18nFallback: 'State of charge',
                                })
                            }}
                        </label>
                        <span
                            class="vrm-block__scroll__icon sg-vrm-icon_021_battery-16px sg-color-green"
                        ></span>
                        <strong>
                            {{
                                propRead(installation, 'bs') ||
                                    $t('common.not_applicable', { i18nFallback: 'Not applicable' })
                            }}
                        </strong>
                        <br />
                        {{ installation.data.soc_status.textValue }}
                    </div>
                    <div v-if="propRead(installation, 'bv')" class="vrm-block__scroll__item">
                        <label class="vrm-block__scroll__label">
                            {{
                                $t('installationoverview.dcVoltage', { i18nFallback: 'DC voltage' })
                            }}
                        </label>
                        <span
                            class="vrm-block__scroll__icon sg-vrm-icon_021_battery-16px sg-color-blue"
                        ></span>
                        <strong>{{ installation.data.bv.formattedValue }}</strong>
                        <br />
                    </div>
                    <div
                        v-if="propRead(installation, 'solar_yield')"
                        class="vrm-block__scroll__item"
                    >
                        <label class="vrm-block__scroll__label">
                            {{
                                $t('installationoverview.solarYield', {
                                    i18nFallback: 'Solar yield',
                                })
                            }}
                        </label>
                        <span
                            class="vrm-block__scroll__icon sg-vrm-icon_022_solar-16px sg-color-orange"
                        ></span>
                        <strong>{{ installation.data.solar_yield.formattedValue }}</strong>
                        <br />
                    </div>
                    <div
                        v-if="propRead(installation, 'consumption')"
                        class="vrm-block__scroll__item"
                    >
                        <label class="vrm-block__scroll__label">
                            {{
                                $t('installationoverview.consumption', {
                                    i18nFallback: 'Consumption',
                                })
                            }}
                        </label>
                        <span
                            class="vrm-block__scroll__icon sg-vrm-icon_024_consumption-16px sg-color-red"
                        ></span>
                        <strong>{{ installation.data.consumption.formattedValue }}</strong>
                        <br />
                    </div>
                    <div
                        v-if="propRead(installation, 'from_to_grid')"
                        class="vrm-block__scroll__item"
                    >
                        <label class="vrm-block__scroll__label">
                            {{ $t('installationoverview.grid', { i18nFallback: 'Grid' }) }}
                        </label>
                        <span
                            class="vrm-block__scroll__icon sg-vrm-icon_023_grid-16px sg-color-red"
                        ></span>
                        <strong>{{ installation.data.from_to_grid.formattedValue }}</strong>
                        <br />
                    </div>
                    <div v-if="propRead(installation, 'pgat')" class="vrm-block__scroll__item">
                        <label class="vrm-block__scroll__label">
                            {{ $t('installationoverview.paygo', { i18nFallback: 'Paygo' }) }}
                        </label>
                        <span
                            v-if="isExpired(installation.data.pgat.formattedValue)"
                            class="vrm-block__scroll__icon sg-vrm-icon_111_paygo-32px sg-color-red"
                        ></span>
                        <span
                            v-if="!isExpired(installation.data.pgat.formattedValue)"
                            class="vrm-block__scroll__icon sg-vrm-icon_111_paygo-32px sg-color-blue"
                        ></span>
                        <strong>
                            {{ getDays(installation.data.pgat.formattedValue) }}
                            <label>{{ $t('overview.days', { i18nFallback: 'days' }) }}</label>
                        </strong>
                        <br />
                        {{ getComment(installation.data.pgat.formattedValue) }}
                    </div>
                </div>
            </vrm-scroll-fade>
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import { ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { Component, Prop } from 'vue-property-decorator';
import moment from 'moment';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade';
import { propRead } from '@/vue/helpers/handleInstallationsValues';

@Component({
    components: { VrmScrollFade },
})
@AngularLocaleBundle('installationoverview')
@AngularLocaleBundle('common')
@AngularLocaleBundle('overview')
@AngularLocaleBundle('core')
export default class VrmInstallationOverviewItem extends Vue {
    @Prop()
    installation;
    @Prop()
    fadeEnabled;
    @ngInject()
    $filter;

    today = moment().startOf('day');
    propRead = propRead;

    convertDeviceIcon(iconCode) {
        return this.$filter('vrmDeviceIcon')(iconCode);
    }

    goToInstallation(id) {
        this.$router.push({ name: 'base.installation.dashboard', params: { id } });
    }

    getComment(timeStamp) {
        const expirationDate = moment(timeStamp * 1000);
        const days = -Math.round(moment.duration(this.today - expirationDate).asDays());
        if (days < 0) {
            return 'Expired';
        } else if (days >= 1) {
            return 'Remaining';
        }
        return '';
    }

    getDays(timeStamp) {
        const expirationDate = moment(timeStamp * 1000);
        const days = -Math.round(moment.duration(this.today - expirationDate).asDays());

        if (days >= 0 && days < 1) {
            return expirationDate.fromNow();
        }

        return Math.abs(days);
    }

    isExpired(timeStamp) {
        const expirationDate = moment(timeStamp * 1000);
        return -Math.round(moment.duration(this.today - expirationDate).asDays()) < 0;
    }

    getLastUpdate(installation) {
        return moment(installation.last_timestamp * 1000).fromNow();
    }
}
</script>
